import React from 'react';
import { Grid } from '@mui/material';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import {
	DateField,
	LanguageSelectField,
	SearchSelectField,
	SimpleTextField,
	useSettings,
	TimeField
} from 'app/main/components';
import { type DetailsSectionInfoContentProps } from 'app/main/components/page-layouts';

export default function UsersDetailsSectionInfo(props: DetailsSectionInfoContentProps) {
	const { t, sysSettings } = useSettings();
	const authorizer = React.useContext(AuthorizerContext);

	const systemInfoAuthorized = authorizer.can('update-systeminfo', 'users');

	if (!props.model.user_start_time) {
		props.sendChange('user_start_time', sysSettings.workday_t_start);
	}
	if (!props.model.user_end_time) {
		props.sendChange('user_end_time', sysSettings.workday_t_end);
	}

	return (
		<>
			{/* NAME */}
			<Grid size={{ lg: 3, sm: 6, xs: 12 }}>
				<SimpleTextField
					name="user_name"
					label={t('DETAILS_USERS_FIELD_NAME')}
					value={props.model.user_name}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* SURNAME */}
			<Grid size={{ lg: 3, sm: 6, xs: 12 }}>
				<SimpleTextField
					name="user_surname"
					label={t('DETAILS_USERS_FIELD_SURNAME')}
					value={props.model.user_surname}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* RECRUITMENT DATE */}
			<Grid size={{ lg: 3, sm: 6, xs: 12 }}>
				<DateField
					name="user_d_recruitment"
					label={t('DETAILS_USERS_FIELD_RECRUITMENT_DATE')}
					value={props.model.user_d_recruitment}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* RETIREMENT DATE */}
			<Grid size={{ lg: 3, sm: 6, xs: 12 }}>
				<DateField
					name="user_d_retirement"
					label={t('DETAILS_USERS_FIELD_RETIREMENT_DATE')}
					value={props.model.user_d_retirement}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* BIRTHDAY */}
			<Grid size={{ lg: 3, sm: 6, xs: 12 }}>
				<DateField
					name="user_birthday"
					label={t('DETAILS_USERS_FIELD_BIRTHDAY')}
					value={props.model.user_birthday}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* TAX CODE */}
			<Grid size={{ lg: 3, sm: 6, xs: 12 }}>
				<SimpleTextField
					name="user_taxcode"
					label={t('DETAILS_USERS_FIELD_TAXCODE')}
					value={props.model.user_taxcode}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* USER COMPANY */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name="user_usco_id"
					label={t('DETAILS_USERS_FIELD_USER_COMPANY')}
					systable="users_companies"
					value={props.model.user_usco_id}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* ROLES */}
			<Grid size={12}>
				<SearchSelectField
					name="user_duties"
					label={t('DETAILS_USERS_FIELD_ROLES')}
					value={props.model.user_duties}
					systable="users_duties"
					multiple
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* LANGUAGE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<LanguageSelectField
					name="user_language"
					value={props.model.user_language}
					userUid={props.model.user_uid}
					isEdit={props.isEdit && systemInfoAuthorized}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* QUALIFICATION */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name="user_usql_id"
					label={t('DETAILS_USERS_FIELD_QUALIFICATION')}
					value={props.model.user_usql_id}
					systable="users_qualifications"
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* START TIME */}
			<Grid size={{ sm: 3, xs: 12 }}>
				<TimeField
					name="user_start_time"
					label={t('USER_WORKDAY_TIME_START')}
					value={props.model.user_start_time}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* END TIME */}
			<Grid size={{ sm: 3, xs: 12 }}>
				<TimeField
					name="user_end_time"
					label={t('USER_WORKDAY_TIME_END')}
					value={props.model.user_end_time}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* LUNCH START TIME */}
			<Grid size={{ sm: 3, xs: 12 }}>
				<TimeField
					name="user_lunch_start_time"
					label={t('USER_LUNCH_TIME_START')}
					value={props.model.user_lunch_start_time}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
				/>
			</Grid>
			{/* LUNCH START TIME */}
			<Grid size={{ sm: 3, xs: 12 }}>
				<TimeField
					name="user_lunch_end_time"
					label={t('USER_LUNCH_TIME_END')}
					value={props.model.user_lunch_end_time}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
				/>
			</Grid>
			{/* HOURLY RATE */}
			{/* <Grid item lg={3} sm={6} xs={12}>
				<CurrencyTextField
					name="user_hourly_rate"
					label={t('DETAILS_USERS_FIELD_HOURLY_RATE')}
					value={props.model.user_hourly_rate}
					isEdit={props.isEdit && systemInfoAuthorized}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* LANGUAGE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<LanguageSelectField
					name="user_language"
					value={props.model.user_language}
					userUid={props.model.user_uid}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* LAYOUT */}
			{/* <Grid item sm={6} xs={12}>
				<LayoutSelectField
					name="user_layout"
					value={props.model.user_layout}
					userUid={props.model.user_uid}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid> */}
			{/* THEME */}
			{/* <Grid item sm={6} xs={12}>
				<ThemeSelectField
					name="user_theme"
					value={props.model.user_theme}
					userUid={props.model.user_uid}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid> */}
		</>
	);
}
